import { useState, useEffect, useCallback } from 'react';
import { API_CONFIG, API_ENDPOINTS } from '../config/api';
import type { FaqCategory, FaqItem, FaqListResponse } from '../types/report';

const SERVER_TO_FRONT_CATEGORY: Record<string, FaqCategory> = {
  'ACCOUNT': 'ACCOUNT_PROFILE',
  'PAYMENT': 'PAYMENT_REFUND',
  'REPORT': 'REPORT_BLOCK',
  'RESTRICTION': 'USAGE_RESTRICTION',
  'MATCHING': 'MATCHING',
  'CHAT': 'CHAT',
  'PRIVACY': 'PRIVACY',
};

export function useFaqList(category?: FaqCategory) {
  const [faqs, setFaqs] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const qs = new URLSearchParams();
    if (category) qs.append('category', category);

    fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.FAQ.LIST}?${qs.toString()}`, {
      credentials: 'include',
    })
      .then((res) => {
        if (!res.ok) throw new Error('FAQ 목록을 불러오지 못했습니다.');
        return res.json() as Promise<FaqListResponse>;
      })
      .then((data) => {
        if (!cancelled) {
          const mapped = data.result.faqs.map((faq) => ({
            ...faq,
            category: (SERVER_TO_FRONT_CATEGORY[faq.category] ?? faq.category) as FaqCategory,
          }));
          setFaqs(mapped);
        }
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.message ?? 'FAQ 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [category]);

  return { faqs, loading, error };
}

export function useSubmitFaqFeedback() {
  const [loading, setLoading] = useState(false);

  const submit = useCallback(async (id: number, helpful: boolean): Promise<void> => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      await fetch(`${API_CONFIG.baseURL}${API_ENDPOINTS.FAQ.FEEDBACK(id)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...(userId && { KEY_USER_ID: userId }),
        },
        credentials: 'include',
        body: JSON.stringify({ helpful }),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return { submit, loading };
}
